import { SectionShell } from "@/components/design-system/SectionShell";
import { DeliveryTicks } from "@/components/ui/delivery-ticks";
import { DateSeparator } from "@/components/ui/date-separator";

const code = `import { DeliveryTicks } from "@/components/ui/delivery-ticks";
import { DateSeparator } from "@/components/ui/date-separator";

// Delivery ticks — keyed off the message lifecycle status:
<DeliveryTicks state="sending" />   {/* single tick — in flight (optimistic) */}
<DeliveryTicks state="sent" />      {/* double tick — persisted by the server */}

// Day divider — label precomputed from a SERVER reference date (SSR-safe):
<DateSeparator label="Hari ini" />
<DateSeparator label="Kemarin" />
<DateSeparator label="9 Juli 2026" />`;

export function ChatSection() {
  return (
    <SectionShell
      id="chat"
      title="Chat"
      description={
        <>
          Messenger primitives for the assistant chat surface (&ldquo;Tanya Mata
          Langit&rdquo;). <code>DeliveryTicks</code> shows a WhatsApp-style
          delivery indicator keyed off the message lifecycle{" "}
          <code>status</code>; <code>DateSeparator</code> is a sticky day divider
          between messages that cross a calendar day. Both are SSR-safe — no clock,
          no browser globals — so labels come from the server, not the client.
        </>
      }
      whenToUse={
        <ul>
          <li>A per-message delivery state on an outgoing (user) turn.</li>
          <li>Grouping a thread by day with a &ldquo;Hari ini&rdquo; / &ldquo;Kemarin&rdquo; / date divider.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>A message-send <em>error</em> — that is a Retry affordance, not a tick state.</li>
          <li>A generic status pill — use <code>Badge</code>.</li>
          <li>Relative time (&ldquo;2 jam lalu&rdquo;) — it drifts between SSR and hydrate; use an absolute stamp.</li>
        </ul>
      }
      preview={
        <div className="flex w-full flex-col gap-6">
          <div className="flex flex-col gap-2">
            <p className="text-sm text-ink-muted">Delivery ticks</p>
            <div className="flex items-end gap-6">
              <div className="flex flex-col items-end gap-1">
                <span className="rounded-lg rounded-tr-sm bg-accent/10 px-3 py-1.5 text-sm text-ink-body">
                  Berapa total primary sales?
                </span>
                <span className="flex items-center gap-1 text-[0.7rem] text-ink-muted">
                  <DeliveryTicks state="sending" /> Mengirim…
                </span>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="rounded-lg rounded-tr-sm bg-accent/10 px-3 py-1.5 text-sm text-ink-body">
                  Berapa total primary sales?
                </span>
                <span className="flex items-center gap-1 text-[0.7rem] text-ink-muted">
                  <span className="tabular-nums">14:32</span> <DeliveryTicks state="sent" />
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-sm text-ink-muted">Day separators</p>
            <div className="flex flex-wrap items-center gap-3">
              <DateSeparator label="Hari ini" className="static" />
              <DateSeparator label="Kemarin" className="static" />
              <DateSeparator label="9 Juli 2026" className="static" />
            </div>
          </div>
        </div>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>
            <code>DeliveryTicks state</code>: <code>sending</code> (single tick) |{" "}
            <code>sent</code> (double tick, accent-colored).
          </li>
          <li>
            <code>DateSeparator label</code>: any string — the caller computes{" "}
            <code>Hari ini</code> / <code>Kemarin</code> / an explicit date from a
            server-provided reference day.
          </li>
        </ul>
      }
    />
  );
}
